import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Descriptions, Divider, Image, Skeleton, Tag, Typography } from "antd";
import { Button } from "evergreen-ui";
import { MDBIcon } from "mdbreact";
import { useListVals } from "../../Firebase/databaseHooks";
import Firebase from "../../Firebase/Firebase";
import { getDatabase, ref } from "firebase/database";
import ReactAudioPlayer from "react-audio-player";
import { getDownloadURL, getStorage, ref as storageRef } from "firebase/storage";
import { getUserName } from "../../Firebase/dataCompat";
import {
	formatConsentDate,
	getConsentMediaReferences,
	normalizeConsentRecord,
} from "../../Pages/FeedbackPages/consentCompat";

const { Item } = Descriptions;
const { Text } = Typography;
const db = getDatabase(Firebase);
const storage = getStorage(Firebase);

function getReferenceLabel(items, id, idFields, labelFields, fallback) {
	if (!id) return fallback;
	const item = (items || []).find((candidate) => idFields.some((field) => candidate?.[field] === id));
	return item ? labelFields.map((field) => item?.[field]).find(Boolean) || fallback : fallback;
}

function isHttpUrl(value) {
	return /^https?:\/\//i.test(String(value || "").trim());
}

async function resolveMediaReference(value) {
	if (!value) return "";
	if (isHttpUrl(value)) return String(value).trim();
	return getDownloadURL(storageRef(storage, value));
}

function isImageUrl(url) {
	return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(decodeURIComponent(String(url || "")));
}

const ViewConsents = ({ consent: rawConsent }) => {
	const [organizationArray, organizationsLoading, organizationsError] = useListVals(ref(db, "talmidsdb/organisations"));
	const [communityArray, communitiesLoading, communitiesError] = useListVals(ref(db, "talmidsdb/communities"));
	const [userArray, usersLoading, usersError] = useListVals(ref(db, "talmidsdb/users"));
	const [countryArray, countriesLoading, countriesError] = useListVals(ref(db, "talmidsdb/countries"));
	const [media, setMedia] = useState({ signature: "", voice: "", additional: [] });
	const [mediaLoading, setMediaLoading] = useState(Boolean(rawConsent));
	const [mediaError, setMediaError] = useState(false);
	const [loadedConsentId, setLoadedConsentId] = useState(null);
	const consent = useMemo(() => normalizeConsentRecord(rawConsent), [rawConsent]);

	useEffect(() => {
		let cancelled = false;
		const consentId = consent.consentRequestID || consent.id;
		const references = getConsentMediaReferences(rawConsent || {});
		setMedia({ signature: "", voice: "", additional: [] });
		setMediaError(false);
		setMediaLoading(Boolean(consentId));

		if (!consentId) {
			setLoadedConsentId(null);
			setMediaLoading(false);
			return () => {
				cancelled = true;
			};
		}

		const loadMedia = async () => {
			const entries = [
				...(references.signature ? [{ kind: "signature", value: references.signature }] : []),
				...(references.voice ? [{ kind: "voice", value: references.voice }] : []),
				...references.additional.map((value) => ({ kind: "additional", value })),
			];
			const results = await Promise.allSettled(entries.map(async (entry) => ({
				...entry,
				url: await resolveMediaReference(entry.value),
			})));
			const resolved = { signature: "", voice: "", additional: [] };
			results.forEach((result) => {
				if (result.status !== "fulfilled" || !result.value.url) return;
				if (result.value.kind === "additional") resolved.additional.push(result.value.url);
				else resolved[result.value.kind] = result.value.url;
			});

			if (!cancelled) {
				setMedia(resolved);
				setMediaError(results.some((result) => result.status === "rejected"));
				setLoadedConsentId(consentId);
				setMediaLoading(false);
			}
		};

		loadMedia().catch(() => {
			if (!cancelled) {
				setMediaError(true);
				setLoadedConsentId(consentId);
				setMediaLoading(false);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [rawConsent, consent.consentRequestID, consent.id]);

	if (!rawConsent) return <Alert message="No consent data available" type="warning" />;

	const loading = organizationsLoading || communitiesLoading || usersLoading || countriesLoading ||
		mediaLoading || loadedConsentId !== (consent.consentRequestID || consent.id);
	if (loading) {
		return (
			<Card variant="borderless">
				<Skeleton active title paragraph={{ rows: 14 }} />
			</Card>
		);
	}

	const lookupError = organizationsError || communitiesError || usersError || countriesError;
	const countryName = getReferenceLabel(countryArray, consent.countryID, ["id", "countryID"], ["countryName", "name"], "Unknown country");
	const communityName = getReferenceLabel(communityArray, consent.communityID, ["id", "communityID"], ["name", "communityName"], "Unknown community");
	const organisationName = getReferenceLabel(organizationArray, consent.organisationID, ["id", "orgID", "organisationID"], ["name", "organisationName"], "Unknown organization");
	const submittedBy = getUserName(userArray, consent.submittedByID, "Unknown user");
	const hasMedia = media.signature || media.voice || media.additional.length;

	return (
		<div className="consent-details-container">
			<Card variant="borderless">
				{lookupError && <Alert message="Some reference details could not be loaded. Available consent data is shown below." type="warning" />}
				<Divider orientation="left">Consent Details</Divider>
				<Descriptions column={1} bordered>
					<Item label="Consent Type">{consent.consentTypeLabel}</Item>
					<Item label="Full Name">{consent.name}</Item>
					<Item label="Consent Given To">{consent.consentTo}</Item>
					<Item label="Phone Number">{consent.phone || "Not provided"}</Item>
					<Item label="Children Names">{consent.childrenNames || "None"}</Item>
					<Item label="Gender">{consent.gender || "Not specified"}</Item>
					<Item label="Country">{countryName}</Item>
					<Item label="Community">{communityName}</Item>
					<Item label="Organization">{organisationName}</Item>
					<Item label="Submitted By">{submittedBy}</Item>
					<Item label="Date Submitted">{formatConsentDate(consent.date)}</Item>
					<Item label="Notes"><div className="notes-content">{consent.notes || "No notes provided"}</div></Item>
				</Descriptions>

				<Divider orientation="left">Consent Permissions</Divider>
				<div className="consent-types-section">
					<Text>Consent Given For:</Text>
					<div className="consent-tags">
						{consent.permissions.length ? consent.permissions.map((type) => (
							<Tag key={type} color="green">{type}</Tag>
						)) : <Tag>None specified</Tag>}
					</div>
					<div className="community-consent">
						<Text>Community Consent: </Text>
						<Tag color={consent.communityConsent ? "green" : "red"}>
							{consent.communityConsent ? "Given" : "Not Given"}
						</Tag>
					</div>
				</div>

				<Divider orientation="left">Consent Documents</Divider>
				{mediaError && <Alert message="Some consent documents could not be loaded." type="warning" className="mb-3" />}
				{hasMedia ? (
					<div className="media-section">
						{media.signature && (
							<div className="media-item">
								<Text>Signature Consent</Text>
								<Image width={300} src={media.signature} alt="Signature Consent" className="media-image" />
								<Button><a href={media.signature} download target="_blank" rel="noreferrer"><MDBIcon icon="download" /> Download Signature</a></Button>
							</div>
						)}
						{media.voice && (
							<div className="media-item">
								<Text strong>Voice Consent Recording</Text>
								<ReactAudioPlayer src={media.voice} controls className="audio-player" />
								<Button><a href={media.voice} download target="_blank" rel="noreferrer"><MDBIcon icon="download" /> Download Audio</a></Button>
							</div>
						)}
						{media.additional.length > 0 && (
							<div className="media-item">
								<Text strong>Additional Documents</Text>
								<div className="other-media-grid">
									{media.additional.map((url, index) => (
										<div key={url} className="media-file">
											{isImageUrl(url) && <Image width={200} src={url} alt={`Consent document ${index + 1}`} className="media-image" />}
											<Button><a href={url} download target="_blank" rel="noreferrer"><MDBIcon icon="download" /> Download Document</a></Button>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				) : <Alert message="No consent documents available" type="info" />}
			</Card>

			<style>{`
				.consent-details-container { max-width: 100%; }
				.notes-content { white-space: pre-wrap; background: #f5f5f5; padding: 12px; border-radius: 4px; }
				.consent-types-section { margin: 16px 0; }
				.consent-tags { margin: 8px 0; display: flex; flex-wrap: wrap; gap: 8px; }
				.community-consent { margin-top: 12px; display: flex; align-items: center; gap: 8px; }
				.media-section, .media-item { display: flex; flex-direction: column; gap: 16px; }
				.media-image { border: 1px solid #ddd; border-radius: 4px; max-width: 100%; }
				.audio-player { width: 100%; max-width: 400px; }
				.other-media-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
			`}</style>
		</div>
	);
};

export default ViewConsents;
